import type {
  Account,
  InventorySnapshotVolume,
  Snapshot,
} from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import type { VolumeConfigState } from './restoreEc2InstanceWizard';
import { ConvertToAMIVolumeConfiguration } from './steps/convertAMIVolumeConfiguration';
import { ImageConfiguration } from './steps/imageConfiguration';
import { SelectTargetAccountStep } from './steps/selectTargetAccount';

import type { Encryption } from '../encryption/encryptionComponent';
import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface ConvertAMIBaseState {
  regionName?: string;
  tags?: Record<string, string>;
  keepOriginalTags?: boolean;
  volumesConfigState?: VolumeConfigState[];
}

export interface ConvertToAMIState {
  name?: string;
  description?: string;
  resourceId: string;
  resourceName: string;
  snapshot: Snapshot;
  supportedCloudProviders: Provider[];
  restoreAccount?: Account;
  sharedEncryption?: Encryption;
  crossStepsState: ConvertAMIBaseState;
  initialState: ConvertAMIBaseState;
  volumes: InventorySnapshotVolume[];
}

const convertToAMIFlow: Step<ConvertToAMIState> = {
  name: 'SelectTargetAccountStep',
  Component: SelectTargetAccountStep,
  next: [
    {
      name: 'ImageConfiguration',
      Component: ImageConfiguration,
      next: [
        {
          name: 'ConvertToAMIVolumeConfiguration',
          Component: ConvertToAMIVolumeConfiguration,
        },
      ],
    },
  ],
};

export const ConvertToAMIWizard = ({
  resourceId,
  resourceName,
  snapshot,
  volumes,
  onClose,
  onBack,
}: {
  resourceId: string;
  resourceName: string;
  snapshot: Snapshot;
  volumes: InventorySnapshotVolume[];
  onClose: () => void;
  onBack: () => void;
}) => {
  const tags = snapshot.resource?.tags || {};
  const volumesSettings: VolumeConfigState[] = volumes.map((vol) => ({
    ...vol,
    isEncrypted: true,
    volumeEncryptionKeyId: '',
    encryptionKeyId: undefined,
    keepOriginalSettings: true,
    keepOriginalTags: true,
    tags: vol.tags || {},
  }));

  return (
    <Wizard<ConvertToAMIState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[convertToAMIFlow]}
      initialState={{
        snapshot,
        resourceId,
        resourceName,
        supportedCloudProviders: [Provider.Aws],
        volumes,
        sharedEncryption: {
          isEncrypted: true,
          encryptionKeyId: undefined,
          arn: undefined,
          mode: 'select',
        },
        crossStepsState: {
          volumesConfigState: volumesSettings,
          tags,
        },
        initialState: {
          regionName:
            snapshot.resource?.properties?.awsEc2?.volumes?.[0]?.region ||
            'us-east-1',
          tags,
          keepOriginalTags: true,
          volumesConfigState: volumesSettings,
        },
      }}
      title={`Convert to AMI`}
      stepperLabels={[
        'Restore account',
        'Image configuration',
        'Volume configuration',
      ]}
    />
  );
};
